import React, { useState, ChangeEvent, useContext } from 'react';
import Dialog from '@mui/material/Dialog';
import { styled } from '@mui/material/styles';
import { ContentExportContext } from 'src/components/BeaconV2Components/ExperimentalLayout/Content/Score/ContentExportContext';
import { useStacklineTheme, StacklineTheme, hexToRgba } from '@stackline/ui';
import { FormControlLabel, CheckboxProps, Divider } from '@mui/material';
import { useLegacyExportLive } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ProductExportModal/legacyExportLive';
import { CloseIcon } from 'src/components/SvgIcons/SvgIcons';
import {
  useExportDropdownOption,
  useExportDataFunction,
  useExportCustomOptionBuilder
} from 'src/components/BeaconV2Components/common/AppExportModal/exportDataHooks';
import { ExportContext } from 'src/providers/ExportContextProvider';
import { ExportDropdownOptionIds } from 'src/components/BeaconV2Components/common/AppExportModal/exportDataConfigData';
import { Link } from 'react-router-dom';
import { useSnackbar } from 'src/utils/Hooks';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BuildExportRequestFunction } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';
import { useTopicExportInjection } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicExports/useTopicExportInjection';
import { GenericErrorMessage } from 'src/components/BeaconV2Components/common/CommonSnackbarMessages/GenericErrorMessage';
import { useExportConfig } from './ExportModalContext';
import StyledCheckbox from 'src/components/BeaconV2Components/common/StyledCheckbox';

interface GenericStyledDialogueProps {
  theme: StacklineTheme;
}

const GenericStyledDialogue = styled(Dialog)<GenericStyledDialogueProps>(({ theme }) => ({
  '& .MuiPaper-root': {
    width: '640px',
    height: '545px',
    boxShadow: `0 0 ${theme.spacing.md} 0 ${hexToRgba(theme.colors.primary, 0.4)}`,
    borderRadius: theme.spacing.sm
  }
}));

export const ExportCheckBox = ({
  label,
  isSelected,
  id,
  onSelect,
  checkboxProps
}: {
  label: string;
  isSelected: boolean;
  id: string;
  onSelect: (e: ChangeEvent<HTMLInputElement>) => void;
  checkboxProps?: CheckboxProps;
}) => {
  const theme = useStacklineTheme();
  return (
    <FormControlLabel
      title={label}
      sx={{
        gap: '8px',
        color: theme.colors.primary,
        height: '26px',
        marginLeft: '0',
        '&.Mui-disabled .MuiFormControlLabel-label': {
          color: '#bec7d3'
        }
      }}
      checked={isSelected}
      label={label}
      value={id}
      control={
        <StyledCheckbox
          iconProps={{
            width: '14px',
            height: '14px'
          }}
          {...checkboxProps}
        />
      }
      key={id}
      onChange={(e: ChangeEvent<HTMLInputElement>) => onSelect(e)}
    />
  );
};

export interface ExportDropdownOption {
  id: ExportDropdownOptionIds;
  label: string;
}

interface AppFilterModalProps {
  open: boolean;
  handleClose: () => void;
  buildExportRequest?: BuildExportRequestFunction;
  exportOptionsSortFn?: (a: ExportDropdownOption[], b?: ExportDropdownOption[]) => ExportDropdownOption[];
}

const AppExportModal = ({ open, handleClose, buildExportRequest, exportOptionsSortFn }: AppFilterModalProps) => {
  const theme = useStacklineTheme();
  const [isIncludeComparison, setIsIncludeComparison] = useState(false);
  let { dropdownOptions } = useExportDropdownOption();
  if (exportOptionsSortFn) {
    dropdownOptions = exportOptionsSortFn(dropdownOptions);
  }

  const [selectedGroupBy, setSelectedGroupBy] = useState<ExportDropdownOptionIds | string>(dropdownOptions[0].id);
  const isLiveExport = useContext(ContentExportContext)?.isLiveExport;
  const [isIncludeCurrentContent, setIsIncludeCurrentContent] = useState(false);
  const legacyExportLive = useLegacyExportLive();
  const {
    getGroupByWeekAvailableFunction,
    forceWeekSeparation,
    shouldDisableComparisonPeriod,
    shouldHideCustomizeSection
  } = useExportCustomOptionBuilder(selectedGroupBy);
  const [isSeparateWeek, setIsSeparateWeek] = useState(forceWeekSeparation);
  const [exportButtonDisabled, setExportButtonDisabled] = useState(false);
  const [shouldShowSeparateDataByWeek, setShouldShowSeparateDataByWeek] = useState(true);
  const { startPolling } = useContext(ExportContext);
  const { showSnackbar } = useSnackbar();
  const { exportRequestFunction } = useExportDataFunction({
    groupByFieldName: isSeparateWeek ? `${selectedGroupBy},weekId` : selectedGroupBy,
    includeCompTimePeriod: isIncludeComparison,
    buildExportRequest
  });

  const { shouldIntercept: originalShouldIntercept, exportTopicData } = useTopicExportInjection();

  // this is overriding shouldIntercept for non topic traffic pages
  const { overrideShouldIntercept } = useExportConfig();
  const shouldIntercept = !overrideShouldIntercept && originalShouldIntercept(selectedGroupBy);

  const handleExportButton = async () => {
    setExportButtonDisabled(true);
    const req = isIncludeCurrentContent
      ? legacyExportLive
      : async () => {
          await exportTopicData({ isSeparatedByWeek: isSeparateWeek, isIncludeComparison });
        };

    if (shouldIntercept || isIncludeCurrentContent) {
      try {
        await req();
        startPolling();
        showSnackbar({ type: 'success', message: 'Your download is processing.' });
        handleClose();
      } catch (error) {
        console.error('Error topic export failed:', error);
        showSnackbar({
          message: <GenericErrorMessage />,
          type: 'error'
        });
      }
      setExportButtonDisabled(false);
    } else {
      exportRequestFunction()
        .then(() => {
          startPolling();
          showSnackbar({ type: 'success', message: 'Your download is processing.' });
          handleClose();
        })
        .catch((error) => {
          console.error('Export failed:', error);
          showSnackbar({
            message: <GenericErrorMessage />,
            type: 'error'
          });
        })
        .finally(() => {
          setExportButtonDisabled(false);
        });
    }
  };

  const handleDropdownChange = (exportDropdownOptionId: ExportDropdownOptionIds) => {
    setSelectedGroupBy(exportDropdownOptionId);
    const isGroupByWeekAvailable = getGroupByWeekAvailableFunction(exportDropdownOptionId);

    setShouldShowSeparateDataByWeek(isGroupByWeekAvailable);
  };

  return (
    // @ts-expect-error - GenericStyledDialogue is not typed
    <GenericStyledDialogue open={open} onClose={handleClose} theme={theme}>
      {/* Modal Header */}
      <>
        <Flex height="72px" justifyContent="center" alignItems="center" paddingX="48px" paddingY="18px">
          <Text variant="h4">Export</Text>
          <CloseIcon
            onClick={handleClose}
            style={{ position: 'absolute', cursor: 'pointer', width: '24px', height: '24px', right: '24px' }}
          ></CloseIcon>
        </Flex>
        <Divider />
      </>

      <Flex flexDirection="column" paddingTop="23.5px" marginLeft="48px" height="400px">
        {/* MODAL DESCRIPTION */}
        <Text variant="subtitle1" sx={{ height: '21px' }}>
          Export Data Structure
        </Text>
        <Flex marginTop="8px">
          <Text variant="body2">Select the level of granularity that you would like in the exported file.</Text>
        </Flex>

        {/* GROUP BY DROPDOWN */}
        <Flex marginTop="24px" alignItems="center">
          <Text sx={{ height: '19px' }} variant="subtitle2">
            Group by:
          </Text>
          <Flex marginLeft="8px">
            <SlDropdownMenu
              defaultLabel={ExportDropdownOptionIds.PRODUCT}
              options={dropdownOptions}
              selectedId={selectedGroupBy}
              onChange={(evt) => handleDropdownChange(evt.id as ExportDropdownOptionIds)}
              stacklineButtonProps={{
                buttonSx: {
                  minWidth: '99px',
                  justifyContent: 'space-between'
                }
              }}
            />
          </Flex>
        </Flex>

        {/* CUSTOMIZED EXPORT OPTION - growth matrix export does not have any Customizable option. */}
        {!shouldHideCustomizeSection && (
          <>
            <Flex marginTop="24px">
              <Text sx={{ height: '19px' }} variant="subtitle2">
                Customize
              </Text>
            </Flex>
            <Flex flexDirection="column" gap={3} marginTop="6.5px">
              {/* Separate data by week Checkbox */}
              {/* forecasts export does not have any Customizable option. */}
              {shouldShowSeparateDataByWeek && (
                <ExportCheckBox
                  isSelected={isSeparateWeek}
                  id="separateWeek"
                  label="Separate data by week"
                  onSelect={(e) => setIsSeparateWeek(e.target.checked)}
                  checkboxProps={{
                    checked: isSeparateWeek || forceWeekSeparation,
                    disabled: forceWeekSeparation || isIncludeCurrentContent
                  }}
                />
              )}
              {/* Include comparison checkbox */}
              {!shouldDisableComparisonPeriod && (
                <ExportCheckBox
                  isSelected={isIncludeComparison}
                  id="includeComparison"
                  label="Include comparison time period"
                  onSelect={(e) => setIsIncludeComparison(e.target.checked)}
                  checkboxProps={{
                    disabled: isIncludeCurrentContent
                  }}
                />
              )}
              {isLiveExport && (
                <ExportCheckBox
                  isSelected={isIncludeCurrentContent}
                  id="includeComparison"
                  label="Include current content"
                  onSelect={(e) => setIsIncludeCurrentContent(e.target.checked)}
                  checkboxProps={{
                    disabled: isIncludeComparison || isSeparateWeek
                  }}
                />
              )}
            </Flex>
          </>
        )}
      </Flex>
      {/* Footer */}
      <>
        <Divider />
        <Flex justifyContent="space-between" alignItems="center" paddingX="48px" paddingY="20px">
          {/* @ts-expect-error - Link is not typed */}
          <Link to="/account/downloads">
            <SlButton sx={{ width: '142px' }}>View downloads</SlButton>
          </Link>
          <SlButton
            buttonSx={{ width: '81px' }}
            variant="contained"
            onClick={handleExportButton}
            disabled={exportButtonDisabled}
          >
            Export
          </SlButton>
        </Flex>
      </>
    </GenericStyledDialogue>
  );
};

export default AppExportModal;
